import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Check, X, CalendarSync, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RescheduleRequests({ onChanged }) {
  const [requests, setRequests] = useState([]);
  const [actingId, setActingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reschedule-requests?status=pending`, { withCredentials: true });
      setRequests(data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching reschedule requests:', e);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const act = async (id, action) => {
    setActingId(id);
    try {
      await axios.post(`${API}/reschedule-requests/${id}/${action}`, {}, { withCredentials: true });
      setRequests((prev) => prev.filter((r) => r.id !== id));
      if (action === 'approve' && onChanged) onChanged();
    } catch (e) {
      alert(`Failed to ${action} the date change. Please try again.`);
    } finally {
      setActingId(null);
    }
  };

  if (requests.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="reschedule-requests-section">
      <h2 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
        <CalendarSync className="w-4 h-4" />
        Date Change Requests ({requests.length})
      </h2>
      {requests.map((req) => (
        <div
          key={req.id}
          className="bg-primary/5 border border-primary/30 rounded-xl p-4"
          data-testid={`reschedule-request-${req.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-medium text-foreground">{req.client_name}</h3>
              <p className="text-sm text-text-secondary">{req.meeting_title}</p>
              <p className="text-xs text-text-muted mt-1 flex items-center gap-1.5 flex-wrap">
                <span>
                  {req.current_date} {req.current_time && `at ${req.current_time}`}
                </span>
                <ArrowRight className="w-3 h-3 text-primary" />
                <span className="text-primary font-medium">
                  {req.requested_date} {req.requested_time && `at ${req.requested_time}`}
                </span>
              </p>
              {req.note && (
                <p className="text-xs text-text-secondary mt-2 italic line-clamp-2">"{req.note}"</p>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => act(req.id, 'approve')}
                disabled={actingId === req.id}
                className="bg-success/15 text-success hover:bg-success/25 border border-success/30 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
                data-testid={`approve-reschedule-${req.id}`}
              >
                <Check className="w-4 h-4" />
                Approve
              </button>
              <button
                onClick={() => act(req.id, 'decline')}
                disabled={actingId === req.id}
                className="bg-alert/10 text-alert hover:bg-alert/20 border border-alert/30 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
                data-testid={`decline-reschedule-${req.id}`}
              >
                <X className="w-4 h-4" />
                Decline
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
